import React, { useState, VFC } from "react";
import { DELETE_NOTIFICATION, DELETE_ALL_NOTIFICATIONS } from "../../graphql/mutation";
import { NotificationList } from "@design-system";
import { useNavigate } from "react-router-dom";
import { NotificationMessageModalContainer } from "../notification-modal/notification-message-modal-container";
import {
  NOTIFICATIONS_QUERY,
  useFindNotificationsQuery,
} from "../../graphql/query/notification-query";
import { useMutation } from "../../hooks";
import { NotificationType } from "../../graphql/types";
import {
  DeleteNotification,
  DeleteNotificationVariables,
} from "../../graphql/__generated_types__/DeleteNotification";
import { DeleteAllNotifications } from "../../graphql/__generated_types__/DeleteAllNotifications";

type NotificationContainerProps = {
  type: NotificationType;
};

type NotificationModalState = {
  show: boolean;
  message: string;
};

export const NotificationsContainer: VFC<NotificationContainerProps> = ({ type }) => {
  const [modalState, setModalState] = useState<NotificationModalState>();
  const { data, refetch, loading } = useFindNotificationsQuery(type);

  const [deleteNotification] = useMutation<DeleteNotification, DeleteNotificationVariables>(
    DELETE_NOTIFICATION,
    {
      update: (cache, mutationResult) => {
        const deletedNotificationId = mutationResult?.data?.deleteNotification.notificationId;
        if (!deletedNotificationId) {
          return;
        }
        const data: any = cache.readQuery({ query: NOTIFICATIONS_QUERY, variables: { type } });
        cache.writeQuery({
          query: NOTIFICATIONS_QUERY,
          data: {
            notifications: data.notifications.filter((n: any) => n.id !== deletedNotificationId),
          },
          variables: { type },
        });
      },
    }
  );
  const [deleteAllNotifications] = useMutation<DeleteAllNotifications>(DELETE_ALL_NOTIFICATIONS, {
    update: (cache) => {
      cache.writeQuery({
        query: NOTIFICATIONS_QUERY,
        data: {
          notifications: [],
        },
        variables: { type },
      });
    },
  });

  const navigate = useNavigate();
  const notifications = data?.notifications || [];
  const notificationListProps = {
    loading: loading,
    onReload: () => refetch(),
    notifications: notifications,
    onViewMessage: (id: number) => {
      setModalState({ show: true, message: notifications?.find((x) => x.id === id)?.message! });
    },
    onViewMatter: (notificationId: number, isParent: boolean) => {
      if (isParent) {
        const matterId = notifications?.find((n: any) => n.id === notificationId)?.matterId;
        navigate(`/parent/${matterId}`);
        return;
      }
      const matterId = notifications?.find((n: any) => n.id === notificationId)?.matterId;
      navigate(`/matter-editor/${matterId}`);
    },
    onDelete: (notificationId: any) => deleteNotification({ variables: { notificationId } }),
    onDeleteAll: deleteAllNotifications,
    onConfigureNotifications: () => navigate("/configure-notifications"),
  };

  return (
    <>
      <NotificationList {...notificationListProps} />
      {modalState?.show && (
        <NotificationMessageModalContainer
          message={modalState.message}
          handleOkClick={() => setModalState({ ...modalState, show: false })}
        />
      )}
    </>
  );
};
